import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, Phone, FileText, Users, Briefcase, User, Search, CheckCircle,
  Clock, ChevronDown, ChevronUp, Download, Loader, Shield, MessageSquare,
} from 'lucide-react'
import api from '../services/api'

// ── Australian WHS Regulators ───────────────────────────────────────────────
const REGULATORS: Record<string, { name: string; phone: string; phone_dial: string; written_hours: number; phone_hours: number; website: string }> = {
  NSW: { name: 'SafeWork NSW',       phone: '13 10 50',    phone_dial: '131050',    phone_hours: 1,  written_hours: 48, website: 'safework.nsw.gov.au' },
  VIC: { name: 'WorkSafe Victoria',  phone: '1800 136 089', phone_dial: '1800136089', phone_hours: 1,  written_hours: 48, website: 'worksafe.vic.gov.au' },
  QLD: { name: 'WHSQ',              phone: '1300 362 128', phone_dial: '1300362128', phone_hours: 1,  written_hours: 48, website: 'worksafe.qld.gov.au' },
  WA:  { name: 'WorkSafe WA',       phone: '1300 307 877', phone_dial: '1300307877', phone_hours: 1,  written_hours: 48, website: 'worksafe.wa.gov.au' },
  SA:  { name: 'SafeWork SA',       phone: '1800 777 209', phone_dial: '1800777209', phone_hours: 1,  written_hours: 48, website: 'safework.sa.gov.au' },
  TAS: { name: 'WorkSafe Tasmania', phone: '1300 366 322', phone_dial: '1300366322', phone_hours: 1,  written_hours: 48, website: 'worksafe.tas.gov.au' },
  ACT: { name: 'WorkSafe ACT',      phone: '02 6207 3000', phone_dial: '0262073000', phone_hours: 1,  written_hours: 48, website: 'worksafe.act.gov.au' },
  NT:  { name: 'NT WorkSafe',       phone: '1800 019 115', phone_dial: '1800019115', phone_hours: 1,  written_hours: 48, website: 'worksafe.nt.gov.au' },
}

// ── Timer helpers ────────────────────────────────────────────────────────────
function useTimer(baseTime: string | null) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  if (!baseTime) return null
  return now - new Date(baseTime).getTime()
}

function formatDuration(ms: number) {
  const totalSecs = Math.floor(ms / 1000)
  const h = Math.floor(totalSecs / 3600)
  const m = Math.floor((totalSecs % 3600) / 60)
  const s = totalSecs % 60
  return `${h}h ${m}m ${s}s`
}

function CountdownTimer({ baseTime, deadlineHours, completed }: { baseTime: string; deadlineHours: number; completed: boolean }) {
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  if (completed) return <span className="text-xs font-bold" style={{ color: '#3DB54A' }}>✓ Completed</span>

  const elapsed = now - new Date(baseTime).getTime()
  const deadline = deadlineHours * 3600 * 1000
  const remaining = deadline - elapsed
  const overdue = remaining < 0
  const pct = Math.min(100, (elapsed / deadline) * 100)
  const urgent = pct >= 80

  const absRemaining = Math.abs(remaining)
  const h = Math.floor(absRemaining / 3600000)
  const m = Math.floor((absRemaining % 3600000) / 60000)
  const s = Math.floor((absRemaining % 60000) / 1000)

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <Clock size={13} color={overdue ? '#D93025' : urgent ? '#F5921E' : '#94A3B8'} />
        <span className="text-xs font-bold" style={{ color: overdue ? '#D93025' : urgent ? '#F5921E' : 'var(--text-secondary)' }}>
          {overdue ? `OVERDUE by ${h}h ${m}m` : `${h}h ${m}m ${s}s remaining`}
        </span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#E2E8F0' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: overdue ? '#D93025' : urgent ? '#F5921E' : '#3DB54A' }} />
      </div>
    </div>
  )
}

// ── Section wrapper ──────────────────────────────────────────────────────────
function Section({ num, title, icon: Icon, complete, children }: { num: number; title: string; icon: React.ElementType; complete: boolean; children: React.ReactNode }) {
  const [open, setOpen] = useState(num <= 3)
  return (
    <div className="rounded-2xl overflow-hidden" style={{ border: `1px solid ${complete ? '#3DB54A44' : 'var(--border)'}`, background: complete ? '#F0FBF1' : 'white' }}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
      >
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold" style={{ background: complete ? '#3DB54A' : '#EBF0F8', color: complete ? 'white' : '#1B2B4B' }}>
          {complete ? '✓' : num}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon size={16} color={complete ? '#3DB54A' : '#1B2B4B'} />
          <span className="font-semibold text-sm" style={{ color: complete ? '#2A7A35' : 'var(--text)' }}>{title}</span>
        </div>
        {open ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 border-t" style={{ borderColor: complete ? '#3DB54A22' : 'var(--border)' }}>
          {children}
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
interface Props {
  incident: any
  onRefresh?: () => void
}

export default function CompliancePanel({ incident, onRefresh }: Props) {
  const [compliance, setCompliance] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [noticeLoading, setNoticeLoading] = useState(false)
  const [noticeDraft, setNoticeDraft] = useState('')
  const [sceneAcked, setSceneAcked] = useState(false)
  const [notifying, setNotifying] = useState<string | null>(null)

  const elapsed = useTimer(incident.created_at)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/incidents/${incident.id}/compliance`)
      setCompliance(data)
    } catch {}
    setLoading(false)
  }, [incident.id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    // Scene ack stored locally per incident
    const key = `scene_ack_${incident.id}`
    if (localStorage.getItem(key)) setSceneAcked(true)
  }, [incident.id])

  const logAction = async (action_type: string, extras: Record<string, string> = {}) => {
    await api.post(`/incidents/${incident.id}/compliance`, { action_type, ...extras }).catch(() => {})
    await load()
  }

  const handleSceneAck = async () => {
    localStorage.setItem(`scene_ack_${incident.id}`, '1')
    setSceneAcked(true)
    await logAction('scene_preserved', { notes: 'Scene preservation acknowledged by user' })
  }

  const handlePhoneNotification = async (jurisdiction: string) => {
    await logAction('regulator_phone', { notes: `Phone notification made to ${REGULATORS[jurisdiction]?.name || jurisdiction}`, contact_type: jurisdiction })
    await api.patch(`/incidents/${incident.id}`, { notification_phone_completed: new Date().toISOString() }).catch(() => {})
    if (onRefresh) onRefresh()
  }

  const generateNotice = async (jurisdiction: string) => {
    setNoticeLoading(true); setNoticeDraft('')
    try {
      const { data } = await api.post(`/incidents/${incident.id}/generate-notice`, { jurisdiction })
      setNoticeDraft(data.notice || data.draft || JSON.stringify(data))
    } catch (err: any) {
      setNoticeDraft(`Error: ${err.response?.data?.error || 'Failed to generate notice'}`)
    }
    setNoticeLoading(false)
  }

  const notifyElt = async (contactId: string, method: string) => {
    setNotifying(contactId)
    try {
      await api.post(`/incidents/${incident.id}/notify-elt`, { contactId, method })
      await load()
    } catch {}
    setNotifying(null)
  }

  if (loading) return (
    <div className="flex items-center justify-center h-20" style={{ color: 'var(--text-muted)' }}>
      <Loader size={16} className="animate-spin mr-2" />Loading compliance data…
    </div>
  )

  // Determine jurisdiction
  const jurisdiction = incident.jurisdiction || incident.site_state || 'NSW'
  const regulator = REGULATORS[jurisdiction] || REGULATORS.NSW
  const actions = compliance?.actions || []
  const contacts = compliance?.contacts || []
  const contracts = compliance?.contracts || []
  const investigation = compliance?.investigation

  const hasAction = (type: string) => actions.some((a: any) => a.action_type === type)
  const phoneComplete = !!(incident.notification_phone_completed) || hasAction('regulator_phone')
  const writtenComplete = !!(incident.notification_written_completed) || hasAction('regulator_written')
  const eltNotified = contacts.length > 0 && contacts.every((c: any) => actions.some((a: any) => a.action_type === 'elt_notified' && a.notes?.includes(c.name)))
  const clientsNotified = contracts.length === 0 || contracts.every((c: any) => actions.some((a: any) => a.action_type === 'client_notified' && a.notes?.includes(c.client_name)))

  // Progress categories
  const categories = [
    { label: 'Scene', complete: sceneAcked || hasAction('scene_preserved') },
    { label: 'Regulatory', complete: phoneComplete && writtenComplete },
    { label: 'ELT', complete: contacts.length === 0 || hasAction('elt_notified') },
    { label: 'Client', complete: clientsNotified },
    { label: 'WC', complete: hasAction('workers_comp_reviewed') || !incident.lost_time },
  ]
  const completedCount = categories.filter(c => c.complete).length
  const overallPct = Math.round((completedCount / categories.length) * 100)

  // Non-notifiable simplified view
  if (!incident.notifiable && (incident.ai_severity !== 'critical' && incident.ai_severity !== 'high')) {
    return (
      <div className="card space-y-4">
        <div className="flex items-center gap-2">
          <Shield size={20} color="#1B2B4B" />
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Compliance Checklist</h2>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: '#E8F8EA', color: '#3DB54A' }}>Non-Notifiable</span>
        </div>
        <div className="space-y-3">
          {[
            { key: 'internal_notified', label: 'Internal notifications sent (supervisor / safety manager)' },
            { key: 'client_notified', label: 'Client / principal contractor notified (if required)' },
            { key: 'corrective_actions_raised', label: 'Corrective actions raised' },
          ].map(({ key, label }) => {
            const done = hasAction(key)
            return (
              <button key={key} onClick={() => !done && logAction(key)}
                className="w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors"
                style={{ background: done ? '#E8F8EA' : 'var(--bg)', border: `1px solid ${done ? '#3DB54A44' : 'var(--border)'}` }}>
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: done ? '#3DB54A' : 'var(--border)' }}>
                  {done && <CheckCircle size={12} color="white" />}
                </div>
                <span className="text-sm font-medium" style={{ color: done ? '#2A7A35' : 'var(--text)' }}>{label}</span>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Master progress header */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Shield size={20} color="#D93025" />
            <h2 className="font-bold text-base" style={{ color: 'var(--text)' }}>Notifiable Incident Compliance</h2>
            <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: '#FFEBE6', color: '#D93025' }}>NOTIFIABLE</span>
          </div>
          {elapsed && (
            <div className="text-right">
              <div className="text-xs font-semibold" style={{ color: elapsed > 3600000 ? '#D93025' : '#F5921E' }}>
                ⏱ {formatDuration(elapsed)} elapsed
              </div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>since incident</div>
            </div>
          )}
        </div>

        {/* Category progress pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map(({ label, complete }) => (
            <div key={label} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
              style={{ background: complete ? '#E8F8EA' : '#FEF3E2', color: complete ? '#3DB54A' : '#F5921E' }}>
              {complete ? <CheckCircle size={11} /> : <Clock size={11} />}
              {label}
            </div>
          ))}
        </div>

        {/* Overall progress bar */}
        <div>
          <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>
            <span>Overall compliance</span><span>{overallPct}%</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--bg)' }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${overallPct}%`, background: overallPct === 100 ? '#3DB54A' : '#1B2B4B' }} />
          </div>
        </div>
      </div>

      {/* SECTION 1 — Scene preservation */}
      <Section num={1} title="Scene Preservation" icon={AlertTriangle} complete={sceneAcked || hasAction('scene_preserved')}>
        <div className="pt-3 p-4 rounded-xl" style={{ background: '#FFEBE6', border: '1px solid #D9302533' }}>
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} color="#D93025" className="flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm" style={{ color: '#D93025' }}>Preserve the scene — do not disturb</p>
              <p className="text-xs mt-1" style={{ color: '#8A2010' }}>
                Under s39 WHS Act, you must not disturb the site of a notifiable incident without permission from the regulator, except to assist injured persons or prevent further harm.
              </p>
            </div>
          </div>
        </div>
        {!sceneAcked && !hasAction('scene_preserved') && (
          <button onClick={handleSceneAck} className="btn btn-danger text-sm flex items-center gap-2">
            <CheckCircle size={15} />I acknowledge — scene is preserved
          </button>
        )}
        {(sceneAcked || hasAction('scene_preserved')) && (
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color: '#3DB54A' }}>
            <CheckCircle size={16} />Scene preservation acknowledged
          </div>
        )}
      </Section>

      {/* SECTION 2 — Regulatory notification */}
      <Section num={2} title={`Regulatory Notification — ${regulator.name}`} icon={Phone} complete={phoneComplete && writtenComplete}>
        <div className="pt-3 space-y-4">
          {/* Phone notification */}
          <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>1. Immediate phone notification</p>
              {!phoneComplete ? (
                <CountdownTimer baseTime={incident.created_at} deadlineHours={regulator.phone_hours} completed={false} />
              ) : (
                <span className="text-xs font-bold" style={{ color: '#3DB54A' }}>✓ Completed</span>
              )}
            </div>
            <a href={`tel:${regulator.phone_dial}`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white w-full justify-center"
              style={{ background: '#1B2B4B', textDecoration: 'none' }}
              onClick={(e) => { e.preventDefault(); window.open(`tel:${regulator.phone_dial}`); if (!phoneComplete) handlePhoneNotification(jurisdiction) }}>
              <Phone size={16} fill="white" />Call {regulator.name} — {regulator.phone}
            </a>
            {!phoneComplete && (
              <button onClick={() => handlePhoneNotification(jurisdiction)}
                className="btn btn-secondary text-xs w-full">
                Mark phone call as completed
              </button>
            )}
          </div>

          {/* Written s38 notice */}
          <div className="p-4 rounded-xl space-y-3" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <div className="flex items-center justify-between">
              <p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>2. Written notice (s38 — 48hrs)</p>
              {!writtenComplete ? (
                <CountdownTimer baseTime={incident.created_at} deadlineHours={regulator.written_hours} completed={false} />
              ) : (
                <span className="text-xs font-bold" style={{ color: '#3DB54A' }}>✓ Completed</span>
              )}
            </div>
            <button onClick={() => generateNotice(jurisdiction)} disabled={noticeLoading}
              className="btn btn-secondary text-sm flex items-center gap-2 w-full justify-center">
              {noticeLoading ? <><Loader size={14} className="animate-spin" />Generating…</> : <><FileText size={14} />Generate AI Draft s38 Notice</>}
            </button>
            {noticeDraft && (
              <div className="rounded-xl p-4 text-xs whitespace-pre-wrap font-mono overflow-auto max-h-64" style={{ background: 'white', border: '1px solid var(--border)', color: 'var(--text)', lineHeight: 1.6 }}>
                {noticeDraft}
              </div>
            )}
            {noticeDraft && (
              <div className="flex gap-2">
                <button onClick={() => { navigator.clipboard.writeText(noticeDraft) }} className="btn btn-secondary text-xs flex-1">Copy</button>
                <button onClick={() => logAction('regulator_written', { notes: 'S38 written notice submitted' })}
                  className="btn btn-primary text-xs flex-1">Mark as Submitted</button>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* SECTION 3 — ELT notifications */}
      <Section num={3} title="ELT / Management Notifications" icon={Users} complete={contacts.length === 0 || hasAction('elt_notified')}>
        <div className="pt-3 space-y-2">
          {contacts.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No notification contacts configured. <Link to="/notifications" className="font-semibold underline" style={{ color: '#1B2B4B' }}>Add contacts →</Link>
            </p>
          )}
          {contacts.map((contact: any) => {
            const notified = actions.some((a: any) => a.action_type === 'elt_notified' && a.notes?.includes(contact.name))
            return (
              <div key={contact.id} className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: notified ? '#E8F8EA' : 'var(--bg)', border: '1px solid var(--border)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{contact.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{contact.role || contact.title} · {contact.email}</p>
                </div>
                {notified ? (
                  <span className="text-xs font-bold" style={{ color: '#3DB54A' }}>✓ Notified</span>
                ) : (
                  <div className="flex gap-1.5">
                    {contact.mobile && (
                      <button onClick={() => notifyElt(contact.id, 'sms')} disabled={notifying === contact.id}
                        className="btn btn-secondary text-xs py-1.5 flex items-center gap-1">
                        <MessageSquare size={11} />SMS
                      </button>
                    )}
                    {contact.email && (
                      <button onClick={() => notifyElt(contact.id, 'email')} disabled={notifying === contact.id}
                        className="btn btn-secondary text-xs py-1.5 flex items-center gap-1">
                        <FileText size={11} />Email
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {contacts.length > 1 && !hasAction('elt_notified') && (
            <button onClick={() => contacts.forEach((c: any) => notifyElt(c.id, 'email'))}
              className="btn btn-primary text-sm w-full">
              Notify All via Email
            </button>
          )}
        </div>
      </Section>

      {/* SECTION 4 — Client notifications */}
      <Section num={4} title="Client / Principal Contractor Notifications" icon={Briefcase} complete={clientsNotified}>
        <div className="pt-3 space-y-2">
          {contracts.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No client contracts configured. <Link to="/settings/contracts" className="font-semibold underline" style={{ color: '#1B2B4B' }}>Add contracts →</Link>
            </p>
          )}
          {contracts.map((contract: any) => {
            const notified = actions.some((a: any) => a.action_type === 'client_notified' && a.notes?.includes(contract.client_name))
            return (
              <div key={contract.id} className="flex items-center justify-between gap-3 p-3 rounded-xl" style={{ background: notified ? '#E8F8EA' : 'var(--bg)', border: '1px solid var(--border)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{contract.client_name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {contract.safety_contact_name} · Notification window: {contract.notification_window_hours}h
                  </p>
                </div>
                {notified ? (
                  <span className="text-xs font-bold" style={{ color: '#3DB54A' }}>✓ Notified</span>
                ) : (
                  <button onClick={() => logAction('client_notified', { notes: `Client notified: ${contract.client_name}`, contact_type: 'client' })}
                    className="btn btn-secondary text-xs py-1.5">
                    Mark Notified
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </Section>

      {/* SECTION 5 — Workers compensation */}
      <Section num={5} title="Workers Compensation" icon={User} complete={hasAction('workers_comp_reviewed') || (!incident.lost_time && !incident.medical_treatment)}>
        <div className="pt-3 space-y-3">
          {(incident.lost_time || incident.medical_treatment) ? (
            <>
              <div className="p-3 rounded-xl" style={{ background: '#FEF3E2', border: '1px solid #F5921E33' }}>
                <p className="text-sm font-semibold mb-1" style={{ color: '#8A4E00' }}>Workers compensation trigger criteria met</p>
                <ul className="text-xs space-y-0.5" style={{ color: '#8A4E00' }}>
                  {incident.lost_time && <li>• Lost time injury — claim notification required within 48 hours</li>}
                  {incident.medical_treatment && <li>• Medical treatment required — workers comp may apply</li>}
                </ul>
              </div>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Insurer for {jurisdiction}: Check your policy or visit <a href="https://return.to.work.sa.gov.au" target="_blank" rel="noopener" style={{ color: '#1B2B4B' }}>state WC scheme</a>
              </p>
              <div className="flex gap-2">
                <Link to={`/workers-comp`} className="btn btn-secondary text-xs flex items-center gap-1.5">
                  <Briefcase size={13} />Open Workers Comp
                </Link>
                <button onClick={() => logAction('workers_comp_reviewed', { notes: 'Workers comp reviewed and claim lodged/assessed' })}
                  className="btn btn-secondary text-xs">
                  Mark Reviewed
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              No lost time or medical treatment — workers comp claim unlikely required. Mark as confirmed:
            </p>
          )}
          {!hasAction('workers_comp_reviewed') && !incident.lost_time && !incident.medical_treatment && (
            <button onClick={() => logAction('workers_comp_reviewed', { notes: 'No lost time — workers comp not applicable' })}
              className="btn btn-secondary text-sm">
              Confirm — No Claim Required
            </button>
          )}
        </div>
      </Section>

      {/* SECTION 6 — Investigation link */}
      <Section num={6} title="Investigation" icon={Search} complete={!!investigation}>
        <div className="pt-3">
          {investigation ? (
            <div className="flex items-center justify-between p-3 rounded-xl" style={{ background: '#EBF0F8', border: '1px solid #1B2B4B22' }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: '#1B2B4B' }}>{investigation.investigation_number}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{investigation.title || 'Investigation in progress'} · {investigation.status}</p>
              </div>
              <Link to={`/investigations/${investigation.id}`} className="btn btn-secondary text-xs">View →</Link>
            </div>
          ) : (
            <>
              <p className="text-sm mb-3" style={{ color: 'var(--text-muted)' }}>A formal investigation should be conducted for all notifiable incidents.</p>
              <Link to={`/investigations/new?incident_id=${incident.id}`} className="btn btn-primary text-sm flex items-center gap-2 w-full justify-center">
                <Search size={15} />Start ICAM Investigation
              </Link>
            </>
          )}
        </div>
      </Section>

      {/* SECTION 7 — Audit trail */}
      <Section num={7} title="Compliance Audit Trail" icon={FileText} complete={actions.length > 0}>
        <div className="pt-3 space-y-2">
          {actions.length === 0 && (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No compliance actions recorded yet.</p>
          )}
          {actions.map((action: any) => (
            <div key={action.id} className="flex items-start gap-3 p-2.5 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <CheckCircle size={14} color="#3DB54A" className="flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>
                  {action.action_type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                </p>
                {action.notes && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{action.notes}</p>}
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>{action.completed_by_name}</p>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  {new Date(action.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))}
          {actions.length > 0 && (
            <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
              Immutable record — {actions.length} action{actions.length !== 1 ? 's' : ''} logged
            </p>
          )}
        </div>
      </Section>
    </div>
  )
}
